import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import TexHubLogo from '@/components/brand/TexHubLogo';
import RegionSelector from '@/components/RegionSelector';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, Search, ArrowLeft, LayoutDashboard, LogOut } from 'lucide-react';

/**
 * Unified public header for all public pages (Landing, Search, Surplus, Pricing, etc.)
 * @param {boolean} showBack - Show back button
 * @param {boolean} showSearch - Show search link
 * @param {string} backPath - Custom back path (default: -1 for history back)
 */
const PublicHeader = ({ showBack = false, showSearch = true, backPath = null }) => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const avatarUrl = user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=0f172a&color=fff`;
  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
    navigate('/');
  };

  const navItems = [
    { href: '/search', label: isUSA ? 'Explore' : 'Explorar' },
    { href: isUSA ? '/pricing' : '/precios', label: isUSA ? 'Plans' : 'Planes' }
  ];

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <div className="w-full px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side */}
          <div className="flex items-center gap-3">
            {showBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="text-slate-600 hover:text-slate-900"
                data-testid="header-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <Link to="/">
              <TexHubLogo size="default" />
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item, i) => (
              <Link 
                key={i}
                to={item.href} 
                className="text-sm font-medium text-slate-600 hover:text-slate-900"
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <RegionSelector variant="minimal" />
            {showSearch && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/search')}
                className="text-slate-600"
              >
                <Search className="w-5 h-5" />
              </Button>
            )}
            {isAuthenticated ? (
              <div className="flex items-center gap-3" data-testid="public-header-user">
                <Button
                  onClick={() => navigate('/dashboard')}
                  style={{ backgroundColor: '#1E4C8E' }}
                  className="hover:opacity-90"
                  data-testid="public-header-dashboard-btn"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  {isUSA ? 'My dashboard' : 'Mi panel'}
                </Button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 transition-colors"
                  data-testid="public-header-avatar"
                >
                  <img src={avatarUrl} alt={user?.name} className="w-8 h-8 rounded-full object-cover" />
                  <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate">{user?.name?.split(' ')[0]}</span>
                </button>
              </div>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} style={{ color: '#1E4C8E' }} data-testid="public-header-login-btn">
                  {isUSA ? 'Login' : 'Entrar'}
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  style={{ backgroundColor: '#34B772' }}
                  className="hover:opacity-90"
                  data-testid="public-header-register-btn"
                >
                  {isUSA ? 'Start free' : 'Empieza gratis'}
                </Button>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="flex md:hidden items-center gap-2">
            <RegionSelector variant="minimal" showLabel={false} />
            {showSearch && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/search')}
                className="text-slate-600"
              >
                <Search className="w-5 h-5" />
              </Button>
            )}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon">
                  <Menu className="w-5 h-5" style={{ color: '#1E4C8E' }} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b">
                    <TexHubLogo size="small" />
                  </div>
                  <nav className="flex-1 p-4">
                    <ul className="space-y-1">
                      {navItems.map((item, i) => (
                        <li key={i}>
                          <Link 
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                  <div className="p-4 border-t space-y-3">
                    {isAuthenticated ? (
                      <>
                        <div className="flex items-center gap-3 px-1 pb-2" data-testid="public-header-mobile-user">
                          <img src={avatarUrl} alt={user?.name} className="w-9 h-9 rounded-full object-cover" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
                            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                          </div>
                        </div>
                        <Button 
                          className="w-full"
                          style={{ backgroundColor: '#1E4C8E' }}
                          onClick={() => { setMobileMenuOpen(false); navigate('/dashboard'); }}
                        >
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          {isUSA ? 'My dashboard' : 'Mi panel'}
                        </Button>
                        <Button variant="outline" className="w-full" onClick={handleLogout}>
                          <LogOut className="w-4 h-4 mr-2" />
                          {isUSA ? 'Log out' : 'Cerrar sesión'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          variant="outline" 
                          className="w-full" 
                          onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                        >
                          {isUSA ? 'Login' : 'Entrar'}
                        </Button>
                        <Button 
                          className="w-full"
                          style={{ backgroundColor: '#34B772' }}
                          onClick={() => { setMobileMenuOpen(false); navigate('/register'); }}
                        >
                          {isUSA ? 'Start free' : 'Empieza gratis'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default PublicHeader;
